<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <!-- Card for creating new promo -->
    <v-card class="mb-6">
      <v-card-title class="text-h6">
        <v-icon class="mr-2">mdi-plus-box</v-icon>
        Tạo mã giảm giá mới
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="addPromo" ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.promo" label="Mã giảm giá" :rules="[v => !!v || 'Bắt buộc']" required />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model.number="form.sale" label="% Giảm giá" type="number" :rules="[v => v > 0 || 'Phải > 0']" required />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model.number="form.max_times" label="Số lần tối đa" type="number" :rules="[v => v > 0 || 'Phải > 0']" required />
            </v-col>
            <v-col cols="12" md="3">
              <v-dialog v-model="menu" width="auto">
                <template #activator="{ props }">
                  <v-text-field
                    v-model="form.expired_at"
                    label="Ngày hết hạn"
                    readonly
                    v-bind="props"
                    :placeholder="'Chọn ngày hết hạn'"
                  />
                </template>
                <v-card>
                  <v-date-picker
                    v-model="form.expired_at"
                    @update:model-value="menu = false"
                    :min="new Date().toISOString().substr(0, 10)"
                  />
                </v-card>
              </v-dialog>
            </v-col>
            <v-col cols="12" md="1" class="d-flex align-center">
              <v-btn color="primary" type="submit" :loading="loading" :disabled="!valid">
                <v-icon class="mr-1">mdi-content-save</v-icon>
                Lưu
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Table for listing promos -->
    <v-card>
      <v-card-title class="text-h6 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-ticket-percent</v-icon>
          Danh sách mã giảm giá
        </div>
        <v-text-field
          v-model="search"
          label="Tìm kiếm mã giảm giá"
          @input="onSearchInput"
          clearable
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          class="search-input"
          style="max-width: 320px;"
        />
      </v-card-title>
      <v-card-text>
        <!-- Desktop Table View -->
        <div v-if="!$vuetify.display.mobile" class="desktop-view">
          <v-table class="elevation-1">
            <thead>
              <tr>
                <th>Mã giảm giá</th>
                <th>% Giảm giá</th>
                <th>Số lần sử dụng</th>
                <th>Ngày hết hạn</th>
                <th>Trạng thái</th>
                <th class="text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="promo in allPromos" :key="promo.id">
                <td class="d-flex align-center">
                  {{ promo.promo }}
                  <v-btn icon size="x-small" class="ml-1" @click="copyPromo(promo.promo)">
                    <v-icon size="16">mdi-content-copy</v-icon>
                  </v-btn>
                </td>
                <td>{{ promo.sale }}%</td>
                <td>{{ promo.used || 0 }}/{{ promo.max_times }}</td>
                <td>{{ formatDate(promo.expired_at) }}</td>
                <td>
                  <v-chip :color="promo.status === 1 ? 'success' : 'error'" size="small">
                    {{ promo.status === 1 ? 'Hoạt động' : 'Không hoạt động' }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-btn icon size="small" :color="promo.status === 1 ? 'error' : 'success'" @click="toggleStatus(promo)">
                    <v-icon>{{ promo.status === 1 ? 'mdi-cancel' : 'mdi-check-circle' }}</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-pagination
            v-model="page"
            :length="totalPages"
            @update:modelValue="onPageChange"
            class="custom-pagination"
          />
        </div>

        <!-- Mobile Card View -->
        <div v-else class="mobile-view">
          <div class="mobile-promos-container" ref="promosContainer">
            <v-card 
              v-for="promo in allPromos" 
              :key="promo.id" 
              class="mb-3 promo-card"
              elevation="2">
              <v-card-text>
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="promo-code font-weight-bold d-flex align-center">
                    {{ promo.promo }}
                    <v-btn icon size="x-small" class="ml-1" @click="copyPromo(promo.promo)">
                      <v-icon size="16">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <v-chip :color="promo.status === 1 ? 'success' : 'error'" size="small">
                    {{ promo.status === 1 ? 'Hoạt động' : 'Không hoạt động' }}
                  </v-chip>
                </div>
                
                <div class="promo-info">
                  <div class="info-row">
                    <v-icon size="16" color="error" class="mr-2">mdi-percent</v-icon>
                    <strong>Giảm giá:</strong>
                    <span class="ml-2 font-weight-bold text-error">{{ promo.sale }}%</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="info" class="mr-2">mdi-counter</v-icon>
                    <strong>Sử dụng:</strong>
                    <span class="ml-2">{{ promo.used || 0 }}/{{ promo.max_times }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="warning" class="mr-2">mdi-calendar-clock</v-icon>
                    <strong>Hết hạn:</strong>
                    <span class="ml-2">{{ formatDate(promo.expired_at) }}</span>
                  </div>
                </div>
                
                <div class="d-flex justify-center mt-3">
                  <v-btn 
                    :color="promo.status === 1 ? 'error' : 'success'" 
                    size="small" 
                    @click="toggleStatus(promo)"
                    block>
                    <v-icon class="mr-2">{{ promo.status === 1 ? 'mdi-cancel' : 'mdi-check-circle' }}</v-icon>
                    {{ promo.status === 1 ? 'Vô hiệu hóa' : 'Kích hoạt' }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- Loading indicator for infinite scroll -->
          <div v-if="loadingMore" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">Đang tải thêm...</div>
          </div>
          
          <!-- End of list indicator -->
          <div v-if="hasReachedEnd && allPromos.length > 0" class="text-center py-4 text-grey">
            <v-icon>mdi-check-circle</v-icon>
            <div>Đã hiển thị tất cả mã giảm giá</div>
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/http';

function formatDateForMySQL(dateStr) {
  // Nếu là dạng YYYY-MM-DD thì chỉ cần thêm giờ
  if (/^\\d{4}-\\d{2}-\\d{2}$/.test(dateStr)) {
    return dateStr + ' 00:00:00';
  }
  // Nếu là ISO string thì parse và format lại
  const d = new Date(dateStr);
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
}

export default {
  name: 'Promo',
  data() {
    return {
      valid: false,
      loading: false,
      menu: false,
      form: {
        promo: '',
        sale: 0,
        max_times: 1,
        expired_at: null,
      },
      promos: [],
      allPromos: [], // For infinite scroll
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      page: 1,
      limit: 10,
      search: '',
      total: 0,
      totalPages: 1,
      pageLoading: false,
      loadingMore: false, // For infinite scroll loading
      hasReachedEnd: false, // Track if we've loaded all data
      currentMobilePage: 1 // Separate page tracking for mobile
    }
  },
  computed: {
    attachTarget() {
      return this.$refs.dateInput ? this.$refs.dateInput.$el : undefined;
    }
  },
  methods: {
    async addPromo() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const payload = {
          ...this.form,
          expired_at: this.form.expired_at ? formatDateForMySQL(this.form.expired_at) : null,
        };
        await axios.post('/promos', payload);
        this.snackbarText = 'Tạo mã giảm giá thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        this.form = { promo: '', sale: 0, max_times: 1, expired_at: null };
        this.fetchPromos(this.page, this.limit, this.search);
      } catch (e) {
        this.snackbarText = 'Tạo mã giảm giá thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
      this.loading = false;
    },
    async fetchPromos(showPageLoading = false, append = false) {
      if (showPageLoading) {
        this.pageLoading = true;
      }
      if (append) {
        this.loadingMore = true;
      }
      
      try {
        const res = await axios.get('/promos', {
          params: { 
            page: append ? this.currentMobilePage : this.page, 
            limit: this.limit, 
            search: this.search 
          }
        });
        
        if (append) {
          // For mobile infinite scroll
          this.allPromos = [...this.allPromos, ...res.data.data];
          this.hasReachedEnd = res.data.data.length < this.limit || (append ? this.currentMobilePage : this.page) >= res.data.totalPages;
        } else {
          // For desktop pagination or initial load
          this.promos = res.data.data;
          this.allPromos = res.data.data;
        }
        
        this.total = res.data.total;
        this.page = res.data.page;
        this.totalPages = res.data.totalPages;
      } catch (e) {
        this.snackbarText = 'Lấy danh sách mã giảm giá thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        if (showPageLoading) {
          this.pageLoading = false;
        }
        if (append) {
          this.loadingMore = false;
        }
      }
    },

    async loadMorePromos() {
      if (this.loadingMore || this.hasReachedEnd) return;
      
      this.currentMobilePage++;
      await this.fetchPromos(false, true);
    },

    resetMobileData() {
      this.allPromos = [];
      this.currentMobilePage = 1;
      this.hasReachedEnd = false;
    },
    async toggleStatus(promo) {
      try {
        await axios.put(`/promos/${promo.id}/status`, { status: promo.status === 0 ? 1 : 0 });
        promo.status = promo.status === 0 ? 1 : 0;
        this.snackbarText = 'Cập nhật trạng thái thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (e) {
        this.snackbarText = 'Cập nhật trạng thái thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },
    copyPromo(code) {
      navigator.clipboard.writeText(code);
      this.snackbarText = 'Đã copy mã giảm giá!';
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    formatDate(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleDateString('vi-VN');
    },
    onSearchInput() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => {
        this.page = 1;
        this.resetMobileData();
        this.fetchPromos(false);
      }, 400);
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchPromos(false);
    },

    setupInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      
      this.$nextTick(() => {
        if (this.$refs.promosContainer) {
          this.observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && this.$vuetify.display.mobile) {
              this.loadMorePromos();
            }
          }, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1
          });
          
          // Create a sentinel element at the bottom
          const sentinel = document.createElement('div');
          sentinel.style.height = '1px';
          sentinel.style.background = 'transparent';
          this.$refs.promosContainer?.appendChild(sentinel);
          this.observer.observe(sentinel);
        }
      });
    }
  },
  mounted() {
    this.fetchPromos(true);
    this.setupInfiniteScroll();
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>

<style scoped>
.search-input {
  max-width: 320px;
  margin-bottom: 0;
}
.v-btn.icon {
  padding: 0 !important;
  min-width: 24px !important;
  height: 24px !important;
  vertical-align: middle;
}
.v-icon {
  font-size: 16px !important;
  vertical-align: middle;
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-promos-container {
  padding: 8px 0;
}

.promo-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.promo-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.promo-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.promo-code {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-promos-container {
    padding: 4px 0;
  }
  
  .promo-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }
  
  .promo-code {
    font-size: 15px;
  }
}
</style>